// schema
import * as ISchema from "@schema";

// libraries
import * as React from "react";
import { connect } from "react-redux";
import { QueryRenderer, createFragmentContainer, ReadyState } from "react-relay";
import { Disposable } from "relay-runtime";

// state
import { IReduxState } from "@reducers/root.reducer";
import { setSettingsDialogOpen, setMenuDrawerOpen } from "@actions/chrome-extension.actions";
import { toggleExpanded, sendInvite, openTeam } from "@actions/settings.actions";
import { setScrollIsLatched } from "@actions/day-view.actions";
import { getOnboardingFlowState } from "@state/onboarding";

// components
import { WebAppWrapper } from "@components/web-app-wrapper";
// eslint-disable-next-line @typescript-eslint/no-var-requires, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
const Div100vh = require("react-div-100vh").default;

// util
import { getCurrentOrg } from "@util/org.util";
import { getRenderTimeZone, ZonedMoment } from "@util/time-zone.util";
import { hashHistory } from "@util/history.util";

// routing
import { withRouteQuery } from "@routing/routes.util";

// internal
import { webAppCalendarQuery } from "./WebAppCalendar.gql";
import {
  webAppCalendarRendererFragments,
  webAppCalendarRendererQuery,
} from "./WebAppCalendarRenderer.gql";
import { IRendererContainer, IRendererProps, IRendererState, HashParams } from "./WebAppCalendar.d";
import { WebAppCalendar } from "./WebAppCalendar";
import { styles } from "./WebAppCalendar.styles";

// material-ui
import { Button, IconButton, Typography } from "@material-ui/core";
import { NavigateBefore, NavigateNext } from "@material-ui/icons";
import withStyles from "@material-ui/core/styles/withStyles";
import { updateEventParentBaseWrappers } from "@subscriptions";
import { ChromeWrapperRendererLight } from "@components/chrome-wrapper";

const CALENDAR_HASH = /\/([a-zA-Z]+)(\/(\d+))?(\/(\d+))?(\/(\d+))?/;

class WebAppCalendarRendererComponent extends React.Component<IRendererProps, IRendererState> {
  private epbwDisposable?: Disposable;

  constructor(props: IRendererProps) {
    super(props);

    this.state = {
      params: this.getParamsFromHash(),
      isLegendOpen: false,
      showWorkingHours: false,
      showFragmentedTime: false,
    };
  }

  private unsubscribe = () => {
    if (this.epbwDisposable) {
      this.epbwDisposable.dispose();
      delete this.epbwDisposable;
    }
  };

  private subscribe = () => {
    const rendererOrg = getCurrentOrg(this.props.viewer);

    if (!rendererOrg) {
      return;
    }

    this.unsubscribe();

    const selectedWeekMoment = this.getSelectedMoment().startOf("week");

    const dates: string[] = new Array(7).fill(null).map((_v, i) => {
      return selectedWeekMoment.clone().add(i, "days").format("YYYY-MM-DD");
    });

    const teamCalendars =
      (rendererOrg.userTeams &&
        rendererOrg.userTeams.__typename !== "GraphEntityError" &&
        rendererOrg.userTeams.list
          .filter(
            (t) =>
              t.userSettings &&
              t.userSettings.__typename !== "GraphEntityError" &&
              t.userSettings.useTeamCalendar &&
              t.settings &&
              t.settings.__typename !== "GraphEntityError" &&
              t.settings.manageTeamCalendar &&
              t.teamCalendarId,
          )
          .map((t) => t.teamCalendarId)) ||
      [];

    this.epbwDisposable = updateEventParentBaseWrappers(this.props.relay.environment, {
      dates,
      orgRelayId: rendererOrg.id,
      calendarIds: [rendererOrg.primaryOrgCalendar, ...teamCalendars],
      timeZone: getRenderTimeZone(),
    });
  };

  private getParamsFromHash = (): HashParams => {
    const match = CALENDAR_HASH.exec(window.location.hash);

    return (
      (match && {
        mode: match[1],
        year: match[3],
        month: match[5],
        day: match[7],
        found: true,
      }) || { mode: "week", found: false }
    );
  };

  // private onCalendarModeChange = (event: any) => {
  //   const params = this.getParamsFromHash();

  //   const additionalRoute = params['year'] && params['month'] && params['day']
  //     ? `/${event.target.value}/${params['year']}/${params['month']}/${params['day']}`
  //     : `/${event.target.value}`;

  //   hashHistory.replace(additionalRoute);

  //   this.setState({
  //     params: {
  //       ...params,
  //       mode: event.target.value,
  //     },
  //   });
  // }

  private onSelectDay = (moment: ZonedMoment) => {
    hashHistory.replace(`/day/${moment.format("YYYY/MM/DD")}`);
    this.props.setScrollIsLatched(false);

    this.setState({
      params: {
        mode: "day",
        year: moment.format("YYYY")!,
        month: moment.format("MM")!,
        day: moment.format("DD")!,
        found: true,
      },
    });
  };

  private gotoToday = () => {
    const calendarMode = this.state.params.mode;

    hashHistory.replace(`/${calendarMode}`);

    this.props.setScrollIsLatched(true);

    this.setState({
      params: {
        mode: calendarMode,
        found: true,
      },
    });
  };

  private gotoPrev = () => {
    const calendarMode = this.state.params.mode;

    let prevMoment: ZonedMoment;

    switch (calendarMode) {
      case "day":
        prevMoment = this.getSelectedMoment().subtract(1, "days");
        break;
      default:
        prevMoment = this.getSelectedMoment().subtract(1, "weeks");
    }

    hashHistory.replace(`/${calendarMode}/${prevMoment.format("YYYY/MM/DD")}`);
    this.props.setScrollIsLatched(false);
    this.setState({
      params: {
        mode: calendarMode,
        year: prevMoment.format("YYYY")!,
        month: prevMoment.format("MM")!,
        day: prevMoment.format("DD")!,
        found: true,
      },
    });
  };

  private gotoNext = () => {
    const calendarMode = this.state.params.mode;

    let nextMoment: ZonedMoment;

    switch (calendarMode) {
      case "day":
        nextMoment = this.getSelectedMoment().add(1, "days");
        break;
      default:
        nextMoment = this.getSelectedMoment().add(1, "weeks");
    }

    hashHistory.replace(`/${calendarMode}/${nextMoment.format("YYYY/MM/DD")}`);
    this.props.setScrollIsLatched(false);
    this.setState({
      params: {
        mode: calendarMode,
        year: nextMoment.format("YYYY")!,
        month: nextMoment.format("MM")!,
        day: nextMoment.format("DD")!,
        found: true,
      },
    });
  };

  private gotoWeek = () => {
    const selectedMoment = this.getSelectedMoment();

    this.setState({
      params: {
        mode: "week",
        year: selectedMoment.format("YYYY")!,
        month: selectedMoment.format("MM")!,
        day: selectedMoment.format("DD")!,
        found: true,
      },
    });

    hashHistory.replace(`/week/${selectedMoment.format("YYYY/MM/DD")}`);
  };

  private getSelectedMoment = () => {
    const { params } = this.state;

    const selectedMoment = new ZonedMoment();

    if (params["year"] && params["month"] && params["day"]) {
      selectedMoment.year(parseInt(params["year"]));
      selectedMoment.month(parseInt(params["month"]) - 1); // Months are 0 indexed.
      selectedMoment.date(parseInt(params["day"]));
    }

    return selectedMoment;
  };

  public componentDidMount() {
    const onboardingState = getOnboardingFlowState(this.props.viewer.user);

    if (onboardingState?.current.state !== "Finished") {
      this.props.router.replace("/app/onboarding");
    } else if (this.props.match.location.pathname.startsWith("/app/settings")) {
      if (this.props.match.params.section) {
        const section = this.props.match.params.section;
        switch (section) {
          case "defineteam":
            this.props.toggleExpanded("expandTeam");
            break;
          case "workinghours":
            if (this.props.match.params.subsection) {
              if (this.props.match.params.subsection === "meetingbreaks") {
                this.props.toggleExpanded("expandMeetingBreaks");
              }
            } else {
              this.props.toggleExpanded("expandHours");
            }
            break;
          case "meetingbreaks":
            this.props.toggleExpanded("expandMeetingBreaks");
            break;
          case "invite":
            const searchParams = new URLSearchParams(window.location.search);
            if (searchParams.has("email")) {
              const email = decodeURIComponent(searchParams.get("email") as string);
              const cleanEmail = email.split("?")[0]; // to guard against poorly formatted utm params getting added to the url
              this.props.sendInvite(cleanEmail);
            }
            this.props.toggleExpanded("expandInvites");
            break;
          case "smartholds":
            this.props.toggleExpanded("expandSmartHolds");
            break;
          case "email":
            this.props.toggleExpanded("expandEmailsAndNotifications");
            break;
          case "integrations":
          case "slack":
          case "asana":
          case "zoom":
            this.props.toggleExpanded("expandIntegrations");
            break;
          case "personalcal":
            this.props.toggleExpanded("expandPersonalCalendar");
            break;
          case "colors":
            this.props.toggleExpanded("expandColorCoding");
            break;
          case "focustime":
            this.props.toggleExpanded("expandFocusTime");
            break;
          case "traveltime":
            this.props.toggleExpanded("expandTravelTime");
            break;
          case "lunch":
            this.props.toggleExpanded("expandLunch");
            break;
          case "orgsettings":
          case "zoomadmin":
            this.props.toggleExpanded("expandOrgSettings");
            break;
          case "team":
            if (this.props.match.params.subsection) {
              const teamId =
                this.props.match.params.subsection === "primary"
                  ? ""
                  : atob(this.props.match.params.subsection);
              const feature = this.props.match.params.subsubsection;
              switch (feature) {
                case "manage":
                  this.props.openTeam(teamId, 2);
                  break;
                case "nomeetingday":
                  this.props.openTeam(teamId, 2, "nomeetingday");
                  break;
                default:
                  this.props.openTeam(teamId);
                  break;
              }
            } else {
              this.props.toggleExpanded("expandTeam");
            }
            break;
          case "plans":
            this.props.toggleExpanded("expandPaidPlans");
            break;
          default:
            break;
        }
      }

      this.props.setSettingsDialogOpen(true);

      this.props.router.push("/app");
    }

    if (!this.state.params.found) {
      hashHistory.replace("/week");
      this.setState({
        params: {
          mode: "week",
          found: true,
        },
      });
    }

    this.subscribe();
  }

  public componentWillUnmount() {
    this.unsubscribe();
  }

  private renderQuery = () => {
    const rendererOrg = getCurrentOrg(this.props.viewer);
    const userId = this.props.viewer?.user?.id;

    const onboardingState = getOnboardingFlowState(this.props.viewer.user);
    const isOnboardingFinished = onboardingState && onboardingState.current.state === "Finished";

    if (!rendererOrg || !isOnboardingFinished) {
      return null;
    }

    const teamCalendars =
      (rendererOrg.userTeams &&
        rendererOrg.userTeams.__typename !== "GraphEntityError" &&
        rendererOrg.userTeams.list
          .filter(
            (t) =>
              t.userSettings &&
              t.userSettings.__typename !== "GraphEntityError" &&
              t.userSettings.useTeamCalendar &&
              t.settings &&
              t.settings.__typename !== "GraphEntityError" &&
              t.settings.manageTeamCalendar &&
              t.teamCalendarId,
          )
          .map((t) => t.teamCalendarId)) ||
      [];

    const calendarIds = [rendererOrg.primaryOrgCalendar, ...teamCalendars];

    const selectedWeekMoment = this.getSelectedMoment();

    return (
      <QueryRenderer
        query={webAppCalendarQuery}
        variables={{
          calendarIds,
          orgRelayId: rendererOrg.id,
          weekStartDates: [selectedWeekMoment.clone().startOf("week").format("YYYY-MM-DD")],
          timeZone: getRenderTimeZone(),
        }}
        environment={this.props.relay.environment}
        render={(readyState: ReadyState) => {
          const org: ISchema.IOrg = readyState && readyState.props && readyState.props.org;
          const viewer: ISchema.IViewer = readyState && readyState.props && readyState.props.viewer;

          return (
            <WebAppCalendar
              onSelectDay={this.onSelectDay}
              mode={this.state.params.mode}
              selectedWeekMoment={selectedWeekMoment}
              rendererOrg={rendererOrg}
              viewer={viewer}
              org={org}
              userId={userId}
              calendarIds={calendarIds}
              match={this.props.match}
              router={this.props.router}
              showFragmentedTime={this.state.showFragmentedTime}
              showWorkingHours={this.state.showWorkingHours}
            />
          );
        }}
      />
    );
  };

  private renderNav = () => {
    const selectedWeekMoment = this.getSelectedMoment();

    return (
      <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
        {this.state.params.mode === "day" && (
          <Button variant="outlined" style={{ marginRight: 18 }} onClick={this.gotoWeek}>
            WEEK
          </Button>
        )}
        <Button variant="outlined" onClick={this.gotoToday}>
          TODAY
        </Button>
        <IconButton onClick={this.gotoPrev}>
          <NavigateBefore />
        </IconButton>
        <IconButton onClick={this.gotoNext}>
          <NavigateNext />
        </IconButton>
        <Typography variant="h6">{selectedWeekMoment.format("MMMM YYYY")}</Typography>
        {/*<Select
        disableUnderline={true}
        className={this.props.classes.select}
        value={this.state.params.mode}
        onChange={this.onCalendarModeChange}
      >
        <MenuItem value='day'>Day</MenuItem>
        <MenuItem value='week'>Week</MenuItem>
      </Select>*/}
      </div>
    );
  };

  public render() {
    // const { showWorkingHours, showFragmentedTime, isLegendOpen } = this.state;

    const isMobile = window.matchMedia("(max-width: 768px)").matches;

    return (
      <Div100vh style={{ height: "100rvh", display: "flex", flexDirection: "row" }}>
        {!isMobile ? (
          <WebAppWrapper
            hideBetaLogo
            hideAdvancedMenu
            hideMenu
            hideLogo
            dontUseV100
            showChromeCard
            viewer={this.props.viewer}
            navChildren={this.renderNav()}
            hideNav={isMobile}
          >
            <div className={this.props.classes.wrapperContainer}>
              {/*!isMobile ? <div className={this.props.classes['legend']} style={{
            transform: this.state.isLegendOpen ? 'translateX(0)' : 'translateX(-100%)',
          }}>
            <div className="form">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showWorkingHours}
                    onChange={() => this.setState({ showWorkingHours: !showWorkingHours })}
                    value="showWorkingHours"
                    color="primary"
                  />
                }
                label="Show Working Hours"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showFragmentedTime}
                    onChange={() => this.setState({ showFragmentedTime: !showFragmentedTime })}
                    value="showFragmentedTime"
                    color="primary"
                  />
                }
                label="Show Fragmented Time"
              />
            </div>
            <div className="toggle" onClick={() => this.setState({ isLegendOpen: !isLegendOpen })}>
              { isLegendOpen
                ? <ChevronLeft />
                : <ChevronRight />
              }
            </div>
          </div> : null */}

              {this.renderQuery()}
            </div>
          </WebAppWrapper>
        ) : null}
        <div className={this.props.classes.assistantContainer}>
          <ChromeWrapperRendererLight
            environment={this.props.relay.environment}
            match={this.props.match}
            router={this.props.router}
          />
        </div>
      </Div100vh>
    );
  }
}

/////////////////
// With Styles //
/////////////////

const WebAppCalendarRendererStyled = withStyles(styles)(WebAppCalendarRendererComponent);

///////////
// Redux //
///////////
function mapStateToProps(state: IReduxState, _ownProps: IRendererContainer) {
  return {
    settingsDialogOpen: state.webExtension.settingsDialogOpen,
    menuDrawerOpen: state.webExtension.menuDrawerOpen,
  };
}

const WebAppCalendarRendererRedux = connect(mapStateToProps, {
  setSettingsDialogOpen,
  setScrollIsLatched,
  toggleExpanded,
  sendInvite,
  openTeam,
  setMenuDrawerOpen,
})(WebAppCalendarRendererStyled);

///////////
// Relay //
///////////
export const WebAppCalendarRenderer = createFragmentContainer(
  WebAppCalendarRendererRedux,
  webAppCalendarRendererFragments,
);

export const WebAppCalendarRendererPage = withRouteQuery(
  WebAppCalendarRenderer,
  webAppCalendarRendererQuery,
);
